import { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ChevronLeft, ChevronRight, GanttChartSquare } from 'lucide-react';
import { PRIORITY_COLOURS } from '@virtuetasks/types';
import { PageHeader } from '@/app/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { useAuth } from '@/features/auth';
import { useProject } from '@/features/projects';
import { useTasks, type TaskView } from '@/features/tasks/api';
import { DesktopOnly } from './DesktopOnly';

const DAY_MS = 86_400_000;
const ROW_HEIGHT = 30;

type Zoom = 'day' | 'week' | 'month';
const COLUMN_WIDTH: Record<Zoom, number> = { day: 34, week: 14, month: 5 };

const iso = (d: Date) => d.toISOString().slice(0, 10);

function startOfUtcDay(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

interface Bar {
  task: TaskView;
  startIndex: number;
  span: number;
  row: number;
}

export function TimelinePage() {
  const { key = '' } = useParams();
  const navigate = useNavigate();
  const { workspace } = useAuth();
  const { data: project } = useProject(key);
  const [zoom, setZoom] = useState<Zoom>('day');
  const [offsetDays, setOffsetDays] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useTasks(
    { projectId: project?._id, limit: 200, includeSubtasks: true },
    { enabled: Boolean(project) },
  );
  const tasks = (data?.items ?? []) as TaskView[];

  const scheduled = useMemo(
    () => tasks.filter((t) => t.dueDate || t.startDate),
    [tasks],
  );

  const { rangeStart, days } = useMemo(() => {
    if (scheduled.length === 0) {
      const start = startOfUtcDay(new Date());
      return { rangeStart: start, days: 30 };
    }
    const dates = scheduled.flatMap((t) =>
      [t.startDate, t.dueDate].filter(Boolean).map((d) => startOfUtcDay(new Date(d as string))),
    );
    const min = new Date(Math.min(...dates.map((d) => d.getTime())) - 3 * DAY_MS);
    const max = new Date(Math.max(...dates.map((d) => d.getTime())) + 3 * DAY_MS);
    return {
      rangeStart: new Date(min.getTime() + offsetDays * DAY_MS),
      days: Math.max(Math.ceil((max.getTime() - min.getTime()) / DAY_MS) + 1, 21),
    };
  }, [scheduled, offsetDays]);

  const columnWidth = COLUMN_WIDTH[zoom];
  const holidays = useMemo(
    () => new Set((workspace?.holidays ?? []).map((h) => h.date.slice(0, 10))),
    [workspace],
  );
  const workingDays = workspace?.workingDays ?? [1, 2, 3, 4, 5, 6];

  const bars: Bar[] = useMemo(() => {
    return scheduled.map((task, index) => {
      const due = task.dueDate ? startOfUtcDay(new Date(task.dueDate)) : null;
      const start = task.startDate ? startOfUtcDay(new Date(task.startDate)) : due;
      if (!start || !due) {
        const only = (start ?? due)!;
        return {
          task,
          startIndex: Math.round((only.getTime() - rangeStart.getTime()) / DAY_MS),
          span: 1,
          row: index,
        };
      }
      const startIndex = Math.round((start.getTime() - rangeStart.getTime()) / DAY_MS);
      const span = Math.max(Math.round((due.getTime() - start.getTime()) / DAY_MS) + 1, 1);
      return { task, startIndex, span, row: index };
    });
  }, [scheduled, rangeStart]);

  const barById = useMemo(() => new Map(bars.map((b) => [b.task._id, b])), [bars]);

  /** Dependency arrows: from the blocker's end to the blocked task's start. */
  const arrows = useMemo(() => {
    const lines: { from: Bar; to: Bar }[] = [];
    for (const bar of bars) {
      for (const blockerId of bar.task.blockedBy) {
        const from = barById.get(blockerId);
        if (from) lines.push({ from, to: bar });
      }
    }
    return lines;
  }, [bars, barById]);

  const todayIndex = Math.round((startOfUtcDay(new Date()).getTime() - rangeStart.getTime()) / DAY_MS);
  const width = days * columnWidth;

  return (
    <DesktopOnly title="Timeline" listHref={`/projects/${key}/list`}>
      <PageHeader
        title={`${project?.icon ?? ''} Timeline`}
        subtitle={`${scheduled.length} scheduled · dependency arrows shown · non-working days shaded`}
        actions={
          <>
            <Button variant="ghost" size="icon-sm" aria-label="Scroll back" onClick={() => setOffsetDays((d) => d - 7)}>
              <ChevronLeft />
            </Button>
            <Button variant="ghost" size="icon-sm" aria-label="Scroll forward" onClick={() => setOffsetDays((d) => d + 7)}>
              <ChevronRight />
            </Button>
            {(['day', 'week', 'month'] as Zoom[]).map((z) => (
              <Button
                key={z}
                variant={zoom === z ? 'subtle' : 'ghost'}
                size="sm"
                onClick={() => setZoom(z)}
              >
                {z}
              </Button>
            ))}
          </>
        }
      />

      <div className="p-4">
        {isLoading ? (
          <Skeleton className="h-[60vh]" />
        ) : scheduled.length === 0 ? (
          <EmptyState
            icon={<GanttChartSquare className="size-6" />}
            title="Nothing is scheduled"
            description="Timeline shows tasks with a start or due date. Add dates to see them here."
          />
        ) : (
          <div className="vt-surface flex overflow-hidden">
            <div className="w-[220px] shrink-0 border-r border-[var(--border)]">
              <div className="h-8 border-b border-[var(--border)] px-2 py-1.5 text-[11px] font-medium uppercase tracking-wide vt-muted">
                Task
              </div>
              {bars.map((bar) => (
                <button
                  key={bar.task._id}
                  type="button"
                  onClick={() => navigate(`/tasks/${bar.task.key}`)}
                  className="flex w-full items-center gap-1.5 truncate border-b border-[var(--border)] px-2 text-left text-[12px] last:border-b-0 hover:bg-[var(--surface-2)]"
                  style={{ height: ROW_HEIGHT }}
                >
                  <span className="vt-mono shrink-0 text-[10px] vt-muted">{bar.task.key}</span>
                  <span className="truncate">{bar.task.title}</span>
                </button>
              ))}
            </div>

            <div ref={scrollRef} className="min-w-0 flex-1 overflow-x-auto">
              <div style={{ width }}>
                <div className="flex h-8 border-b border-[var(--border)]">
                  {Array.from({ length: days }, (_, i) => {
                    const day = new Date(rangeStart.getTime() + i * DAY_MS);
                    const nonWorking = !workingDays.includes(day.getUTCDay()) || holidays.has(iso(day));
                    return (
                      <div
                        key={i}
                        className="shrink-0 border-r border-[var(--border)] text-center text-[9px] vt-muted"
                        style={{
                          width: columnWidth,
                          background: nonWorking ? 'var(--surface-2)' : undefined,
                        }}
                        title={iso(day)}
                      >
                        {zoom === 'day' ? day.getUTCDate() : i % 7 === 0 ? day.getUTCDate() : ''}
                      </div>
                    );
                  })}
                </div>

                <div className="relative" style={{ height: bars.length * ROW_HEIGHT }}>
                  {/* Non-working day shading behind the bars. */}
                  <div className="absolute inset-0 flex">
                    {Array.from({ length: days }, (_, i) => {
                      const day = new Date(rangeStart.getTime() + i * DAY_MS);
                      const nonWorking = !workingDays.includes(day.getUTCDay()) || holidays.has(iso(day));
                      return (
                        <div
                          key={i}
                          className="shrink-0 border-r border-[var(--border)]"
                          style={{
                            width: columnWidth,
                            background: nonWorking ? 'var(--surface-2)' : undefined,
                          }}
                        />
                      );
                    })}
                  </div>

                  {todayIndex >= 0 && todayIndex < days && (
                    <div
                      className="absolute top-0 z-10 h-full w-px"
                      style={{ left: todayIndex * columnWidth + columnWidth / 2, background: 'var(--accent)' }}
                      aria-label="Today"
                    />
                  )}

                  <svg className="pointer-events-none absolute inset-0 z-10" width={width} height={bars.length * ROW_HEIGHT}>
                    {arrows.map(({ from, to }, i) => {
                      const x1 = (from.startIndex + from.span) * columnWidth;
                      const y1 = from.row * ROW_HEIGHT + ROW_HEIGHT / 2;
                      const x2 = to.startIndex * columnWidth;
                      const y2 = to.row * ROW_HEIGHT + ROW_HEIGHT / 2;
                      const mid = x1 + Math.max((x2 - x1) / 2, 8);
                      return (
                        <path
                          key={i}
                          d={`M ${x1} ${y1} H ${mid} V ${y2} H ${x2}`}
                          fill="none"
                          stroke="var(--text-muted)"
                          strokeWidth="1"
                          markerEnd="url(#vt-arrow)"
                          opacity="0.55"
                        />
                      );
                    })}
                    <defs>
                      <marker id="vt-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                        <path d="M0,0 L6,3 L0,6 Z" fill="var(--text-muted)" />
                      </marker>
                    </defs>
                  </svg>

                  {bars.map((bar) => (
                    <button
                      key={bar.task._id}
                      type="button"
                      onClick={() => navigate(`/tasks/${bar.task.key}`)}
                      title={`${bar.task.key} — ${bar.task.title}`}
                      className="absolute z-20 truncate rounded-[4px] px-1.5 text-left text-[10px] text-white"
                      style={{
                        left: bar.startIndex * columnWidth,
                        width: Math.max(bar.span * columnWidth - 2, 8),
                        top: bar.row * ROW_HEIGHT + 5,
                        height: ROW_HEIGHT - 10,
                        background: PRIORITY_COLOURS[bar.task.priority],
                        opacity: bar.task.statusCategory === 'done' ? 0.45 : 1,
                      }}
                    >
                      {zoom === 'day' ? bar.task.title : ''}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DesktopOnly>
  );
}
